/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {createContext} from "react";
import {BaseComponent, RoutingModule} from "@intuitionrobotics/thunderstorm/frontend";
import {OnCareConfigQueryReturned} from "@modules/GoalsConfigModule";
import {Route_AdminOrganizations, Route_AdminPatients, Route_AdminPermission, Route_AdminUser, Route_Default} from "../../../Routes";
import {OnUsersUpdated} from "@modules/UsersModule";
import {TabButton} from "../../widget/TabButton";
import {padding_rl_lg} from "@styles/styles";
import {AccountModule} from "@intuitionrobotics/user-account/frontend";
import {AppPermissionsModule} from "@modules/AppPermissionsModule";
import {adminRoles, DB_Permission} from "@app/app-shared/permission";
import {Loader} from "../../../../widgets/Loader";
import {AppPermissions} from "../../../utils/Permissions";

type PermissionContextType = {
    permissions: DB_Permission[]
    upsertPermission: (permission: DB_Permission) => void
};
export const PermissionsContext = createContext<PermissionContextType>({
    permissions: [],
    upsertPermission: () => {
    }
})

type State = {
    permissions: DB_Permission[]
    loading: boolean
};

export class AdminTab
    extends BaseComponent<{}, State>
    implements OnCareConfigQueryReturned, OnUsersUpdated {

    constructor(props: {}) {
        super(props);
        this.state = {
            permissions: [],
            loading: true
        };
    }

    componentDidMount() {
        AccountModule.listUsers();
        AppPermissionsModule.list().then(p => this.setState({permissions: p, loading: false})).catch(this.logError);
    }

    __onUsersUpdated = () => this.forceUpdate();
    __onCareConfigQueryReturned = () => this.forceUpdate();

    renderAdminPatientsTab = (selectedKey: string) => {
        return <TabButton
            title={"Customers"}
            selected={selectedKey === Route_AdminPatients}
            onClick={() => {
                RoutingModule.goToRoute(Route_AdminPatients);
            }}
        />;
    };

    renderAdminUserTab = (selectedKey: string) => {
        return <TabButton
            title={"Users"}
            selected={selectedKey === Route_AdminUser}
            onClick={() => {
                RoutingModule.goToRoute(Route_AdminUser);
            }}
        />
    };
    renderAdminPermissionTab = (selectedKey: string) => {
        return <TabButton
            title={"Permissions"}
            selected={selectedKey === Route_AdminPermission}
            onClick={() => {
                RoutingModule.goToRoute(Route_AdminPermission);
            }}
        />;
    };

    renderAdminOrganizationTab = (selectedKey: string) => {
        return <TabButton
            title={"Organizations"}
            selected={selectedKey === Route_AdminOrganizations}
            onClick={() => {
                RoutingModule.goToRoute(Route_AdminOrganizations);
            }}
        />;
    };

    renderHeader = () => {
        const selectedKey = RoutingModule.getMyRouteKey() || Route_Default;
        return <div className={"ll_h_c match_width"}>
            <div className={"ll_h_c match_height"}
                 style={{
                     justifyContent: "space-evenly",
                     flexBasis: "0%",
                     flexGrow: 1,
                     boxSizing: "border-box",
                     marginLeft: 20,
                     borderRadius: 5
                 }}>
            </div>
            <div className={"ll_h_c match_height"} style={{boxSizing: "border-box"}}>
                {this.renderAdminPatientsTab(selectedKey)}
                {this.renderAdminUserTab(selectedKey)}
                {this.renderAdminPermissionTab(selectedKey)}
                {this.renderAdminOrganizationTab(selectedKey)}
            </div>
            <div className={"ll_h_c match_height"} style={{
                flexBasis: "0%", flexGrow: 1, boxSizing: "border-box", justifyContent: "flex-end"
            }}/>
        </div>;
    };

    private renderBody = () => (
        <div className={"ll_v_s"} style={{flex: 1, overflowY: "auto", zIndex: 2, width: "100%"}}>
            <div className={`ll_v_s ${padding_rl_lg}`} style={{flex: 1, position: "relative"}}>
                {RoutingModule.getRoutesMap(
                    [
                        Route_AdminUser,
                        Route_AdminPermission,
                        Route_AdminPatients,
                        Route_AdminOrganizations
                    ])}
            </div>
        </div>);

    private upsertPermission = (permission: DB_Permission) => {
        this.setState(s => {
            const permissions = s.permissions;
            const index = permissions.findIndex(p => p.id === permission.id);
            if (index !== -1)
                permissions[index] = permission;
            else
                permissions.push(permission);
            return {permissions}
        })
    }

    render() {
        return <AppPermissions
            appRoles={adminRoles}
            fallback={RoutingModule.redirect(Route_Default)}
        >
            <PermissionsContext.Provider value={{permissions: this.state.permissions, upsertPermission: this.upsertPermission}}>
                <div
                    className={"fill ll_v_l"}
                    style={{backgroundColor: "white", boxShadow: "0 0 3px 1px #ececec", overflow: "auto"}}
                >
                    {this.renderHeader()}
                    {this.renderBody()}
                </div>
                {this.state.loading && <Loader absolute={true}/>}
            </PermissionsContext.Provider>
        </AppPermissions>;
    }
}

